import { useEffect, useRef, type ReactNode } from "react";
import { useSetAtom } from "jotai";
import { X } from "lucide-react";

import { getText } from "shared/lib";
import { shouldShowValidationVar } from "./lib";
import { type TreeDescriptor, treeName } from "../lib";

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "shared/components";

export interface TreeDialog2Props {
  children?: ReactNode;
  onClickSubmit?: (treeDescriptor: TreeDescriptor) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  subTitle?: string;
  treeDescriptor: TreeDescriptor;
}

export function TreeDialog2({
  children,
  onClickSubmit,
  onOpenChange,
  open,
  subTitle,
  treeDescriptor,
}: TreeDialog2Props) {
  const ref = useRef<HTMLDivElement>(null);

  const setShouldShowValidationVar = useSetAtom(shouldShowValidationVar);

  const checkIfValid = () => {
    const invalidLabels = Array.from(
      ref.current?.querySelectorAll("label[aria-invalid=true]") || [],
    );

    if (invalidLabels.length > 0) {
      setShouldShowValidationVar(true);
      invalidLabels[0]?.scrollIntoView({ behavior: "smooth" });
      return false;
    }

    setShouldShowValidationVar(false);
    return true;
  };

  useEffect(() => {
    setShouldShowValidationVar(false);
  }, [open]); // eslint-disable-line

  const renderHeader = () => {
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between">
          <DialogTitle asChild>
            <h2 className="text-headingxl overflow-hidden truncate text-ellipsis">
              {treeName(treeDescriptor)}
            </h2>
          </DialogTitle>

          <DialogClose
            className="cursor-pointer"
            onClick={() => onOpenChange?.(false)}
          >
            <X className="h-5 w-5" />
          </DialogClose>
        </div>

        {subTitle && <p className="text-bodylg text-textSubdued">{subTitle}</p>}
      </div>
    );
  };

  return (
    <Dialog open={open}>
      <DialogContent
        hideCloseButton
        className="flex max-h-[calc(100%-4rem)] flex-col gap-0 px-0 pb-0 pt-5 md:min-w-[680px] md:pt-8"
        aria-describedby={undefined}
      >
        <div className="flex flex-col overflow-hidden">
          <div className="flex flex-1 flex-col gap-5 overflow-auto pb-5">
            <div className="px-5 md:px-8">{renderHeader()}</div>

            <div className="flex flex-col gap-5 px-5 md:px-8" ref={ref}>
              {children}
            </div>
          </div>
        </div>

        <div className="border-interfaceBorder bg-interfaceBgDefault flex items-center justify-end rounded-b-md border-t px-5 py-3 md:px-10 md:py-3">
          <Button
            onClick={() => {
              const valid = checkIfValid();
              if (valid) {
                onClickSubmit?.(treeDescriptor);
              }
            }}
          >
            {getText("Save and close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
